
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { adminLoginAction, forgotPasswordAction, getCurrentUserAction, loginAction, registerAction, resetPasswordAction, switchWorkspaceAction } from "../actions";
import { bypassUserLoginAction, resetEmployeePasswordAction } from "../actions/AuthActionsNew";
import { Agency } from "./AgencySlice";
import { isSessionInvalid } from "../../utils/authErrors";

export interface Workspace {
    id: string;
    name: string;
    customDomain: string | null;
    logoUrl: string | null;
    role: string;
    ownerId: string;
    ownerEmail: string;
    active: boolean;
}

interface AuthState {
    user: any;
    token: string;
    activeWorkspaceId: string | null;
    workspaces: Workspace[];
    role: "user" | "admin" | "manager" | null;
    loading: boolean;
    error: {
        message: string;
        success: boolean;
    };
    checkinpro: boolean;
    typeLoading: boolean;
    type: Type | null;
    agency: Partial<Agency> | null;
    modules: UserModules | null;
}

export interface UserModules {
    rank_tracker: boolean;
    ctr: boolean;
    map_checkins: boolean;
    white_label: boolean;
    billing: boolean;
    team: boolean;
    dashboard: boolean;
    book_call: boolean;
    reselling?: boolean;
}

export interface Type {
    _id: string;
    first_name: string;
    last_name: string;
    test: boolean;
    email: string;
    type: "user" | "admin";
    redirect_url: string;
    privacyPolicyUrl?: string;
    termsOfConditionUrl?: string;
    map_checkin_enabled: boolean;
    freeUser: boolean;
    agency: Partial<Agency>;
    host: string;
    modules: UserModules;
    role: string;
}



export interface SMTPConfig {
    host: string;
    port: number;
    user: string;
    pass: string;
}

export interface Branding {
    primaryColor: string;
    secondaryColor: string;
}


const errorVal = {
    message: "",
    success: true
}
const initialState: AuthState = {
    user: null,
    token: localStorage.getItem("token_admin") || localStorage.getItem("token_user") || "",
    activeWorkspaceId: localStorage.getItem("activeWorkspaceId") || null,
    workspaces: [],
    role: null,
    loading: false,
    error: errorVal,
    checkinpro: false,
    typeLoading: false,
    type: null,
    agency: null,
    modules: null
};

const agency: Agency = {
    _id: "",
    added_by: "",
    name: "Map Ranking",
    logoUrl: null,
    // customDomain: "app6.aidemoproject.com",
    customDomain: window.location.origin.split("//")[1],
    domainVerified: true,
    smtp: {
        host: "smtp.gmail.com",
        port: 587,
        user: "",
        pass: "",
    },
    branding: {
        primaryColor: "",
        secondaryColor: "",
    },
    status: "active",
    createdAt: "",
    updatedAt: "",
    __v: 0,
};


const authSlice2 = createSlice({
    name: "auth2",
    initialState,
    reducers: {
        logout: (state, action: PayloadAction<"admin" | "user" | undefined>) => {
            const session = action.payload || state.role;
            if (session === "admin" || session === "manager") localStorage.removeItem("token_admin");
            else localStorage.removeItem("token_user");
            state.user = null;
            state.token = "";
            state.activeWorkspaceId = null;
            localStorage.removeItem("activeWorkspaceId");
            state.workspaces = [];
            state.role = null;
            state.checkinpro = false;
            state.modules = null;
        },

        restoreSession: (state, action: PayloadAction<"admin" | "user">) => {
            const session = action.payload;
            const token = localStorage.getItem(session === "admin" ? "token_admin" : "token_user");
            if (token) {
                state.token = token;
                state.role = session;
                state.user = null; // will be fetched later with GetCurrentUser
                state.activeWorkspaceId = localStorage.getItem("activeWorkspaceId") || null;
            }
        },
    },
    extraReducers: (builder) => {
        // Register
        builder.addCase(registerAction.pending, (state) => {
            state.loading = true;
            state.error = errorVal;
            state.user = null;
            state.checkinpro = false

        });
        builder.addCase(registerAction.fulfilled, (state, action) => {
            state.loading = false;
            state.user = action.payload?.data?.user;
            state.token = action.payload?.data?.token;
            state.activeWorkspaceId = action.payload?.data?.activeWorkspaceId;
            if (state.activeWorkspaceId) {
                localStorage.setItem("activeWorkspaceId", state.activeWorkspaceId);
            }
            state.workspaces = action.payload?.data?.workspaces || [];
            state.role = "user";
            state.checkinpro = action?.payload?.data?.user?.map_checkin_enabled


            localStorage.setItem("token_user", state.token);
            // state.error = {
            //     message: action?.payload?.message,
            //     success: action?.payload?.success
            // }
        });
        builder.addCase(registerAction.rejected, (state, action) => {
            state.loading = false;
            state.user = null;
            state.checkinpro = false

        });

        // User Login
        builder.addCase(loginAction.pending, (state) => {
            state.loading = true;
            state.error = errorVal;
            state.user = null;
            state.checkinpro = false

        });
        builder.addCase(loginAction.fulfilled, (state, action) => {
            state.loading = false;
            state.user = action.payload?.data?.user;
            state.token = action.payload?.data?.token;
            state.activeWorkspaceId = action.payload?.data?.activeWorkspaceId;
            if (state.activeWorkspaceId) {
                localStorage.setItem("activeWorkspaceId", state.activeWorkspaceId);
            }
            state.workspaces = action.payload?.data?.workspaces || [];
            state.role = "user";
            localStorage.setItem("token_user", state.token);
            state.checkinpro = action?.payload?.data?.user?.map_checkin_enabled

            // state.error = {
            //     message: action?.payload?.message,
            //     success: action?.payload?.success
            // }
        });
        builder.addCase(loginAction.rejected, (state) => {
            state.loading = false;
            state.user = null;
            state.checkinpro = false

            // state.error = state?.error;
        });

        // Admin/Manager Login
        builder.addCase(adminLoginAction.pending, (state) => {
            state.loading = true;
            state.error = errorVal;
            state.user = null;
        });
        builder.addCase(adminLoginAction.fulfilled, (state, action) => {
            state.loading = false;
            state.user = action.payload?.data?.user;
            state.token = action.payload?.data?.token;
            state.activeWorkspaceId = action.payload?.data?.activeWorkspaceId || action.payload?.data?.user?.activeWorkspaceId;
            if (state.activeWorkspaceId) {
                localStorage.setItem("activeWorkspaceId", state.activeWorkspaceId);
            }
            state.workspaces = action.payload?.data?.workspaces || [];
            state.role = action.payload?.data?.user?.type || null;
            localStorage.setItem("token_admin", state.token);
            // state.error = {
            //     message: action?.payload?.message,
            //     success: action?.payload?.success
            // }
        });
        builder.addCase(adminLoginAction.rejected, (state) => {
            state.loading = false;
            state.user = null;
            // state.error = errorVal;

        });

        //forgot password
        builder.addCase(forgotPasswordAction.pending, (state) => {
            state.loading = true;
            state.error = errorVal;
        });
        builder.addCase(forgotPasswordAction.fulfilled, (state, action) => {
            state.loading = false;
            state.error = errorVal;
        });
        builder.addCase(forgotPasswordAction.rejected, (state) => {
            state.loading = false;
            state.error = errorVal;
        });

        //reset password
        builder.addCase(resetPasswordAction.pending, (state) => {
            state.loading = true;
            state.error = errorVal;
        });
        builder.addCase(resetPasswordAction.fulfilled, (state, action) => {
            state.loading = false;
            state.error = errorVal;
        });
        builder.addCase(resetPasswordAction.rejected, (state) => {
            state.loading = false;
            state.error = errorVal;
        });
        //reset Employee Password
        builder.addCase(resetEmployeePasswordAction.pending, (state) => {
            state.loading = true;
            state.error = errorVal;
        });
        builder.addCase(resetEmployeePasswordAction.fulfilled, (state, action) => {
            state.loading = false;
            state.error = errorVal;
        });
        builder.addCase(resetEmployeePasswordAction.rejected, (state) => {
            state.loading = false;
            state.error = errorVal;
        });

        // Get Current User
        builder.addCase(getCurrentUserAction.pending, (state) => {
            state.loading = true;
            state.error = errorVal;
            state.user = null;
            state.checkinpro = false
            state.type = null
            state.agency = null
            state.typeLoading = true

        });
        builder.addCase(getCurrentUserAction.fulfilled, (state, action) => {
            state.loading = false;
            state.user = action.payload.data;
            state.role = action.payload.data.type;
            state.checkinpro = action?.payload?.data?.map_checkin_enabled;
            state.type = action?.payload?.data;
            state.agency = action?.payload?.data?.agency || agency;
            state.activeWorkspaceId = action.payload.data.activeWorkspaceId || action.payload.data.agency?._id;
            if (state.activeWorkspaceId) {
                localStorage.setItem("activeWorkspaceId", state.activeWorkspaceId);
            }
            state.workspaces = action.payload.data.workspaces;
            state.modules = action.payload.data.modules;
            state.typeLoading = false;
        });
        builder.addCase(getCurrentUserAction.rejected, (state, action) => {
            state.loading = false;
            state.typeLoading = false;
            if (!isSessionInvalid(action.payload)) {
                // Network/5xx failure: keep the session so a retry can recover it
                return;
            }
            state.user = null;
            state.token = "";
            state.role = null;
            // localStorage.removeItem("token_admin");
            // localStorage.removeItem("token_user");
            state.error = errorVal;
            state.type = null
            state.agency = agency
            state.typeLoading = false
        });

        // Admin Bypass User Login
        builder.addCase(bypassUserLoginAction.pending, (state) => {
            // Do not set global loading, as it unmounts ProtectedRoutes
            state.error = errorVal;
            // state.checkinpro = false; // We probably shouldn't reset checkinpro either for the admin here
        });
        builder.addCase(bypassUserLoginAction.fulfilled, (state, action) => {
            // no-op for global loading
        });
        builder.addCase(bypassUserLoginAction.rejected, (state) => {
            // no-op for global loading
        });

        // Switch Workspace
        builder.addCase(switchWorkspaceAction.pending, (state) => {
            state.loading = true;
            state.modules = null;
        });
        builder.addCase(switchWorkspaceAction.fulfilled, (state, action) => {
            state.loading = false;
            state.user = action.payload?.data?.user;
            state.token = action.payload?.data?.token;
            state.activeWorkspaceId = action.payload?.data?.activeWorkspaceId;
            if (state.activeWorkspaceId) {
                localStorage.setItem("activeWorkspaceId", state.activeWorkspaceId);
            }
            state.workspaces = action.payload?.data?.workspaces || [];
            state.role = "user";
            localStorage.setItem("token_user", state.token);
            state.checkinpro = action?.payload?.data?.user?.map_checkin_enabled
        });
        builder.addCase(switchWorkspaceAction.rejected, (state, action) => {
            state.loading = false;
        });
    },
});

export const { logout, restoreSession, } = authSlice2.actions;
export default authSlice2.reducer;
